// Accessible haunted house theme with WCAG AA compliance and company branding
export const THEME = {
  bg: {
    primary: 'bg-gray-900',           // #111827 - Main dark background
    secondary: 'bg-gray-800',         // #1f2937 - Secondary areas
    tertiary: 'bg-gray-700',          // #374151 - Cards/panels
    accent: 'bg-emerald-600',         // Company green for buttons (#059669)
    hover: 'hover:bg-gray-600',       // #4b5563 - Interactive hover states
    focus: 'focus:bg-gray-600',       // Focus states for keyboard navigation
    disabled: 'bg-gray-800',          // Disabled state background
  },
  text: {
    primary: 'text-gray-50',          // #f9fafb - Main text (19.56:1 contrast ratio)
    secondary: 'text-gray-100',       // #f3f4f6 - Secondary text (18.7:1 contrast)
    accent: 'text-red-300',           // #fca5a5 - Haunted theme headers (11.89:1 contrast)
    highlight: 'text-amber-200',      // #fde68a - Questions/important text (14.78:1 contrast)
    success: 'text-emerald-300',      // #6ee7b7 - Company green success text (12.6:1 contrast)
    muted: 'text-gray-300',           // #d1d5db - Muted text (9.74:1 contrast - still WCAG AA)
    onAccent: 'text-white',           // #ffffff - Text on colored backgrounds
    link: 'text-emerald-300',         // Company green for links
    linkHover: 'hover:text-emerald-200', // Link hover state
    error: 'text-red-300',            // Error messages (11.89:1 contrast)
    warning: 'text-amber-300',        // Warning messages (13.55:1 contrast)
  },
  border: {
    primary: 'border-gray-600',       // #4b5563 - Main borders
    secondary: 'border-gray-500',     // #6b7280 - Secondary borders
    accent: 'border-emerald-500',     // Company green borders
    hover: 'hover:border-gray-400',   // Interactive hover
    focus: 'focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50', // Focus indicators
    error: 'border-red-400',          // Error state borders
    success: 'border-emerald-400',    // Success state borders
  },
  interactive: {
    // Button variants
    button: {
      primary: 'bg-emerald-600 hover:bg-emerald-700 focus:bg-emerald-700 text-white border-emerald-600 hover:border-emerald-700',
      secondary: 'bg-gray-700 hover:bg-gray-600 focus:bg-gray-600 text-gray-100 border-gray-600 hover:border-gray-500',
      danger: 'bg-red-600 hover:bg-red-700 focus:bg-red-700 text-white border-red-600 hover:border-red-700',
      ghost: 'bg-transparent hover:bg-gray-700 focus:bg-gray-700 text-gray-100 border-gray-500 hover:border-gray-400',
    },
    // Form elements
    input: 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50',
    select: 'bg-gray-800 border-gray-600 text-gray-100 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50',
    checkbox: 'text-emerald-600 bg-gray-800 border-gray-600 focus:ring-emerald-400 focus:ring-2 focus:ring-opacity-50',
  },
  // Accessibility utilities
  accessibility: {
    // Skip links for keyboard navigation
    skipLink: 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-emerald-600 text-white px-4 py-2 rounded z-50',
    // Screen reader only text
    srOnly: 'sr-only',
    // Focus visible for custom elements
    focusVisible: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-opacity-75',
    // High contrast mode support
    highContrast: 'motion-safe:transition-colors',
  },
  // Status and feedback colors
  status: {
    success: 'bg-emerald-900 border-emerald-500 text-emerald-200',
    error: 'bg-red-900 border-red-500 text-red-200',
    warning: 'bg-amber-900 border-amber-500 text-amber-200',
    info: 'bg-blue-900 border-blue-500 text-blue-200',
  },
  // Animation preferences
  motion: {
    // Respect reduced motion preference
    transition: 'motion-safe:transition-all motion-safe:duration-200',
    hover: 'motion-safe:hover:scale-105',
    focus: 'motion-safe:focus:scale-105',
  }
};

// Accessibility helper functions
export const A11Y_HELPERS = {
  // Generate ARIA label for buttons
  buttonLabel: (action, context) => `${action}${context ? ` ${context}` : ''}`,
  
  // Generate descriptive text for screen readers
  describeElement: (type, state, additional) => {
    let description = type;
    if (state) description += `, ${state}`;
    if (additional) description += `, ${additional}`;
    return description;
  },
  
  // Check if user prefers reduced motion
  prefersReducedMotion: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  
  // Announce to screen readers
  announce: (message, priority = 'polite') => {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.textContent = message;
    document.body.appendChild(announcer);
    setTimeout(() => document.body.removeChild(announcer), 1000);
  }
};